import {
  Alert,
  Box,
  Button,
  FormControl,
  FormHelperText,
  Grid,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  TextField,
  Typography,
} from "@mui/material";
import React, { useContext, useEffect, useState } from "react";

import { AppContext } from "../context/AppContext";

const REQUEST_TYPES = [
  "Appointment Visit",
  "Private Party",
  "Listening Party",
  "Event or Showcase",
  "Meeting or Workshop",
  "Photo or Video Shoot",
  "Brand Activation",
  "Other",
];

const OFFERINGS = [
  { icon: "🎉", title: "Private Parties", blurb: "Celebrate in a relaxed, cannabis-friendly space." },
  { icon: "🎧", title: "Listening Parties", blurb: "High vibes, good sound, good company." },
  { icon: "🎤", title: "Events & Showcases", blurb: "Comedy, live music, karaoke, paint nights & more." },
  { icon: "💼", title: "Meetings & Workshops", blurb: "Connect, collaborate & create." },
  { icon: "📸", title: "Photo & Video Shoots", blurb: "Creative content, captured your way." },
  { icon: "⭐", title: "Brand Activations", blurb: "Let's build something unforgettable." },
];

const initialFormState = {
  name: "",
  email: "",
  phone: "",
  requestType: "",
  preferredDate: "",
  preferredTime: "",
  guestCount: "",
  message: "",
};

export const BookingRequest = () => {
  const { member } = useContext(AppContext);
  const [formData, setFormData] = useState(initialFormState);
  const [errors, setErrors] = useState({});
  const [submitting, setSubmitting] = useState(false);
  const [submitSuccess, setSubmitSuccess] = useState(false);
  const [submitError, setSubmitError] = useState(false);

  useEffect(() => {
    if (member) {
      setFormData((prev) => ({
        ...prev,
        name: prev.name || member.displayName || "",
        email: prev.email || member.email || "",
      }));
    }
  }, [member]);

  const handleChange = (event) => {
    const { name, value } = event.target;
    setFormData((prev) => ({ ...prev, [name]: value }));
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: null }));
    }
  };

  const validateForm = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = "Name is required";
    if (!formData.email) {
      newErrors.email = "Email is required";
    } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
      newErrors.email = "Invalid email address";
    }
    if (!formData.phone) newErrors.phone = "Phone number is required";
    if (!formData.requestType)
      newErrors.requestType = "Please choose a request type";
    if (!formData.preferredDate)
      newErrors.preferredDate = "Preferred date is required";

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async () => {
    if (!validateForm()) return;

    setSubmitting(true);
    setSubmitError(false);
    try {
      const response = await fetch(
        "https://us-central1-manageyourclub-973b2.cloudfunctions.net/submitBookingRequest",
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
          },
          body: JSON.stringify(formData),
        }
      );

      const result = await response.json();

      if (!result.success) {
        throw new Error(result.error || "Failed to submit request");
      }

      setSubmitSuccess(true);
      setFormData({
        ...initialFormState,
        name: member?.displayName || "",
        email: member?.email || "",
      });
      setErrors({});
    } catch (error) {
      console.error("Error submitting booking request:", error);
      setSubmitError(true);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <Paper elevation={3} sx={{ p: 3, margin: "0 auto" }}>
      <Typography
        variant="h4"
        component="h2"
        align="center"
        gutterBottom
        color="primary"
      >
        Open by Appointment, Events & Private Bookings Only
      </Typography>
      <Typography variant="body1" align="center" sx={{ mb: 3 }}>
        The Cola is now available for appointments, private bookings, and
        scheduled events. Request a visit or book the space below &mdash; and
        you can still grab tickets to our upcoming events.
      </Typography>

      <Grid container spacing={2} sx={{ mb: 3 }}>
        {OFFERINGS.map((offering) => (
          <Grid item xs={12} sm={6} md={4} key={offering.title}>
            <Box sx={{ display: "flex", gap: 1.5, alignItems: "flex-start" }}>
              <Typography sx={{ fontSize: "1.5rem", lineHeight: 1 }}>
                {offering.icon}
              </Typography>
              <Box>
                <Typography variant="subtitle2" fontWeight="bold">
                  {offering.title}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {offering.blurb}
                </Typography>
              </Box>
            </Box>
          </Grid>
        ))}
      </Grid>

      <Typography variant="h5" component="h3" gutterBottom>
        Request an Appointment or Booking
      </Typography>

      {submitSuccess && (
        <Alert severity="success" sx={{ mb: 2 }}>
          Thank you! We&apos;ve received your request and will get back to you
          within 24 hours.
        </Alert>
      )}

      {submitError && (
        <Alert severity="error" sx={{ mb: 2 }}>
          There was an error submitting your request. Please try again.
        </Alert>
      )}

      <Grid container spacing={2}>
        <Grid item xs={12} sm={6}>
          <TextField
            name="name"
            label="Full Name"
            fullWidth
            required
            value={formData.name}
            onChange={handleChange}
            error={!!errors.name}
            helperText={errors.name}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="email"
            label="Email"
            type="email"
            fullWidth
            required
            value={formData.email}
            onChange={handleChange}
            error={!!errors.email}
            helperText={errors.email}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <TextField
            name="phone"
            label="Phone Number"
            fullWidth
            required
            value={formData.phone}
            onChange={handleChange}
            error={!!errors.phone}
            helperText={errors.phone}
          />
        </Grid>

        <Grid item xs={12} sm={6}>
          <FormControl fullWidth required error={!!errors.requestType}>
            <InputLabel>What would you like to book?</InputLabel>
            <Select
              name="requestType"
              value={formData.requestType}
              onChange={handleChange}
              label="What would you like to book?"
            >
              {REQUEST_TYPES.map((type) => (
                <MenuItem key={type} value={type}>
                  {type}
                </MenuItem>
              ))}
            </Select>
            {errors.requestType && (
              <FormHelperText>{errors.requestType}</FormHelperText>
            )}
          </FormControl>
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            name="preferredDate"
            label="Preferred Date"
            type="date"
            fullWidth
            required
            InputLabelProps={{ shrink: true }}
            value={formData.preferredDate}
            onChange={handleChange}
            error={!!errors.preferredDate}
            helperText={errors.preferredDate}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            name="preferredTime"
            label="Preferred Time"
            type="time"
            fullWidth
            InputLabelProps={{ shrink: true }}
            value={formData.preferredTime}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12} sm={4}>
          <TextField
            name="guestCount"
            label="Estimated Guest Count"
            type="number"
            fullWidth
            value={formData.guestCount}
            onChange={handleChange}
          />
        </Grid>

        <Grid item xs={12}>
          <TextField
            name="message"
            label="Tell Us About Your Visit or Event"
            multiline
            rows={4}
            fullWidth
            value={formData.message}
            onChange={handleChange}
            placeholder="Tell us more about what you have in mind and any special requests..."
          />
        </Grid>

        <Grid item xs={12}>
          <Button
            variant="contained"
            color="primary"
            size="large"
            fullWidth
            onClick={handleSubmit}
            disabled={submitting}
          >
            {submitting ? "Submitting..." : "Submit Request"}
          </Button>
        </Grid>
      </Grid>
    </Paper>
  );
};

export default BookingRequest;
